import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiError } from "@/lib/api";
import { Loader2, Lock } from "lucide-react";

const BRAND_LOGO = "/api/uploads/logo_konzade_full.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="hidden lg:block relative bg-black">
        <img
          src="/api/uploads/login_store.webp"
          alt="Konzade Endüstriyel Mutfak Mağazası"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="bg-white rounded-2xl px-5 py-4 inline-block shadow-lg"><Logo size="lg" logoUrl={BRAND_LOGO} /></div>
          <div>
            <div className="w-16 h-1.5 bg-rose-600 mb-6" />
            <h2 className="font-heading text-4xl font-black text-white leading-tight">
              Endüstriyel Mutfak<br />Çözümlerinde Güç
            </h2>
            <p className="text-gray-300 mt-4 max-w-md">
              Müşteri, ürün ve teklif yönetiminizi tek panelden yönetin.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-gray-50">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo size="md" logoUrl={BRAND_LOGO} />
          </div>
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-black tracking-tight">Giriş Yap</h1>
            <p className="text-sm text-slate-500 mt-2">CRM paneline erişmek için giriş yapın.</p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">E-posta</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@konzade.com" required data-testid="login-email" autoComplete="email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Şifre</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="login-password" autoComplete="current-password" />
            </div>
            {error && (
              <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-rose-600 hover:bg-rose-700 text-white h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" />Giriş Yap</>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
